package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.CourseFiles
import com.evoapps.evoshift.domain.model.CourseKind
import com.evoapps.evoshift.domain.model.CourseProblem
import com.evoapps.evoshift.domain.model.CoursePage
import com.evoapps.evoshift.domain.model.CoursePageKind
import com.evoapps.evoshift.domain.model.CoursePresentation
import com.evoapps.evoshift.domain.model.PresentationFiles
import com.evoapps.evoshift.domain.model.PresentationLinkProblem
import com.evoapps.evoshift.domain.model.PresentationLinkResult
import com.evoapps.evoshift.domain.model.PresentationLinks
import com.evoapps.evoshift.domain.model.PresentationSource
import com.evoapps.evoshift.domain.model.RichDocument
import com.evoapps.evoshift.domain.model.CourseProgressReport
import com.evoapps.evoshift.domain.model.CourseResult
import com.evoapps.evoshift.domain.model.CourseRules
import com.evoapps.evoshift.domain.model.CourseCompletion
import com.evoapps.evoshift.domain.model.CourseProgress
import com.evoapps.evoshift.domain.model.InvalidCourseException
import com.evoapps.evoshift.domain.model.MyCourse
import com.evoapps.evoshift.domain.model.OhsConfirmation
import com.evoapps.evoshift.domain.model.OhsCourseInfo
import com.evoapps.evoshift.domain.model.OhsEntry
import com.evoapps.evoshift.domain.model.OhsRecord
import com.evoapps.evoshift.domain.model.OhsRules
import com.evoapps.evoshift.domain.model.OhsWorkerGroup
import com.evoapps.evoshift.domain.model.QuizQuestion
import com.evoapps.evoshift.domain.model.TrainingStatus
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.OhsDocumentKind
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.courses.AssignCourseUseCase
import com.evoapps.evoshift.domain.usecase.courses.ConfirmOhsTrainingUseCase
import com.evoapps.evoshift.domain.usecase.courses.DeleteCourseUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetCourseForEditingUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetCourseProgressReportUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetCourseToTakeUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetManagedCoursesUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetMyCoursesUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetOhsDocumentUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetOhsRecordUseCase
import com.evoapps.evoshift.domain.usecase.courses.GetTrainingStatusesUseCase
import com.evoapps.evoshift.domain.usecase.courses.LoadCourseImageUseCase
import com.evoapps.evoshift.domain.usecase.courses.NewCourseIdUseCase
import com.evoapps.evoshift.domain.usecase.courses.RecordOhsTrainingUseCase
import com.evoapps.evoshift.domain.usecase.courses.SaveCourseProgressUseCase
import com.evoapps.evoshift.domain.usecase.courses.SaveCourseUseCase
import com.evoapps.evoshift.domain.usecase.courses.SubmitCourseUseCase
import com.evoapps.evoshift.domain.usecase.courses.UploadCourseImageUseCase
import com.evoapps.evoshift.domain.usecase.courses.UploadCoursePresentationUseCase
import com.evoapps.evoshift.domain.usecase.courses.LoadCoursePresentationUseCase
import com.evoapps.evoshift.documents.DocumentOpener
import com.evoapps.evoshift.domain.usecase.documents.PickedFile
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toDocumentUiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.course_assigned_message
import evoshift.composeapp.generated.resources.course_deleted
import evoshift.composeapp.generated.resources.course_image_invalid
import evoshift.composeapp.generated.resources.course_presentation_invalid
import evoshift.composeapp.generated.resources.course_presentation_pdf_invalid
import evoshift.composeapp.generated.resources.course_published
import evoshift.composeapp.generated.resources.course_saved
import evoshift.composeapp.generated.resources.documents_file_unreadable
import evoshift.composeapp.generated.resources.ohs_confirmed
import evoshift.composeapp.generated.resources.ohs_recorded
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.random.Random

/*
 * SH-126: the training screens' view models. Who sees what: [Gate.TAKE_COURSES] (everyone working
 * there: their courses and OHS record), [Gate.COURSE_PROGRESS] (managers: results, statuses,
 * records) and [Gate.MANAGE_COURSES] (managers while the plan runs: writing courses, assigning,
 * recording OHS parts). The server grades and keeps the OHS record (functions/courses.js).
 */

private fun systemToday(): LocalDate = AppClock.todayIn(TimeZone.currentSystemDefault())

/** A new id for a page or a question (unique within its course). */
internal fun newItemId(prefix: String): String = prefix + Random.nextLong(1L shl 40, Long.MAX_VALUE).toString(36)

// --- The training hub (More → Training) ---------------------------------------------------------

/** A person of the team with their training status (null: nothing that counts is assigned). */
data class TeamTrainingRow(val userId: String, val name: String, val status: TrainingStatus?)

data class CoursesUiState(
    val isLoading: Boolean = true,
    val canTake: Boolean = false,
    val canManage: Boolean = false,
    val canSeeProgress: Boolean = false,
    val myCourses: List<MyCourse> = emptyList(),
    val myOhs: OhsRecord? = null,
    /** SH-244: the reader is the owner: their OHS record holds only the periodic training. */
    val myOhsIsOwner: Boolean = false,
    val managed: List<Course> = emptyList(),
    val team: List<TeamTrainingRow> = emptyList(),
    val today: LocalDate = LocalDate(2026, 1, 1),
    val error: UiText? = null
)

/** The training hub: the reader's own courses and OHS record; for managers the courses and the team. */
class CoursesViewModel(
    private val workplaceId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getMyCourses: GetMyCoursesUseCase,
    private val getManagedCourses: GetManagedCoursesUseCase,
    private val getOhsRecord: GetOhsRecordUseCase,
    private val getTrainingStatuses: GetTrainingStatusesUseCase,
    private val users: UserRepository,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val today: () -> LocalDate = ::systemToday
) : ViewModel() {
    private val _uiState = MutableStateFlow(CoursesUiState(today = today()))
    val uiState: StateFlow<CoursesUiState> = _uiState.asStateFlow()
    private val job = JobSlot()
    private var user: User? = null

    init {
        job.launch(viewModelScope) {
            combine(getCurrentUser(), observePlanActive.activeAt(workplaceId)) { u, active -> u to active }
                .distinctUntilChanged()
                .collect { (u, active) -> load(u, active) }
        }
    }

    private suspend fun load(user: User?, planActive: Boolean) {
        this.user = user
        val capabilities = user?.capabilitiesAt(workplaceId, planActive).orEmpty()
        _uiState.update {
            it.copy(
                canTake = capabilities.shows(Gate.TAKE_COURSES),
                canManage = capabilities.shows(Gate.MANAGE_COURSES),
                canSeeProgress = capabilities.shows(Gate.COURSE_PROGRESS)
            )
        }
        if (user == null || capabilities.isEmpty()) {
            _uiState.update { it.copy(isLoading = false) }
            return
        }
        refresh()
    }

    /** Reloads everything (also after coming back from a course or the editor). */
    fun refresh() {
        val user = user ?: return
        viewModelScope.launch {
            val state = _uiState.value
            val mine = if (state.canTake) getMyCourses(workplaceId, user) else Result.success(emptyList())
            // SH-244: the owner is the employer: no initial training to confirm, but their periodic
            // training may be recorded (the card then shows no warnings, see myOhsIsOwner).
            val ohs = if (state.canTake) getOhsRecord(workplaceId, user.uid).getOrNull() else null
            val managed = if (state.canSeeProgress) getManagedCourses(workplaceId) else Result.success(emptyList())
            val team = if (state.canSeeProgress) teamRows() else emptyList()
            val error = (mine.exceptionOrNull() ?: managed.exceptionOrNull())?.toUiText()
            _uiState.update {
                it.copy(
                    isLoading = false,
                    myCourses = mine.getOrDefault(emptyList()),
                    myOhs = ohs,
                    myOhsIsOwner = !OhsRules.appliesTo(user, workplaceId),
                    managed = managed.getOrDefault(emptyList()),
                    team = team,
                    today = today(),
                    error = error
                )
            }
        }
    }

    private suspend fun teamRows(): List<TeamTrainingRow> {
        val statuses = getTrainingStatuses(workplaceId).getOrDefault(emptyMap())
        // SH-244: owners are the employer, not people to give the initial training: not listed.
        return users.getAssignableEmployees(workplaceId).getOrDefault(emptyList())
            .filter { OhsRules.appliesTo(it, workplaceId) }
            .sortedBy { it.displayName.lowercase() }
            .map { TeamTrainingRow(it.uid, it.displayName, statuses[it.uid]) }
    }

    fun messageShown() = _uiState.update { it.copy(error = null) }
}

// --- The dashboard card --------------------------------------------------------------------------

data class MyTrainingUiState(
    val workplaceId: String? = null,
    /** Courses not passed yet. */
    val toDo: Int = 0,
    /** Of them, past their day. */
    val overdue: Int = 0,
    /** OHS instructions waiting for the person's confirmation. */
    val confirmations: Int = 0
) {
    val shows: Boolean get() = workplaceId != null && (toDo > 0 || confirmations > 0)
}

/** "Training: 2 to do" on the dashboards, for the session's workplace. */
@OptIn(ExperimentalCoroutinesApi::class)
class MyTrainingViewModel(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val getMyCourses: GetMyCoursesUseCase,
    private val getOhsRecord: GetOhsRecordUseCase,
    private val today: () -> LocalDate = ::systemToday
) : ViewModel() {
    private val _uiState = MutableStateFlow(MyTrainingUiState())
    val uiState: StateFlow<MyTrainingUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            combine(getCurrentUser(), sessionManager.currentWorkplaceId) { u, wp -> u to wp }
                .distinctUntilChanged { a, b -> a.first?.uid == b.first?.uid && a.second == b.second && a.first?.engagements == b.first?.engagements }
                .collect { (user, wp) ->
                    if (user == null || wp == null || !user.capabilitiesAt(wp).shows(Gate.TAKE_COURSES)) {
                        _uiState.value = MyTrainingUiState()
                        return@collect
                    }
                    val courses = getMyCourses(wp, user).getOrDefault(emptyList()).filterNot { it.passed }
                    val ohs = if (OhsRules.appliesTo(user, wp)) getOhsRecord(wp, user.uid).getOrNull() else null
                    _uiState.value = MyTrainingUiState(
                        workplaceId = wp,
                        toDo = courses.size,
                        overdue = courses.count { it.overdueOn(today()) },
                        confirmations = ohs?.pendingConfirmations?.size ?: 0
                    )
                }
        }
    }
}

// --- The editor ----------------------------------------------------------------------------------

data class CourseEditorUiState(
    val isLoading: Boolean = true,
    val canEdit: Boolean = false,
    val isNew: Boolean = true,
    val course: Course? = null,
    /** Question id → the right options. */
    val correct: Map<String, List<Int>> = emptyMap(),
    /** The workplace's positions, to assign the course by. */
    val roles: List<String> = emptyList(),
    val isSaving: Boolean = false,
    /** The page whose picture or presentation is uploading. */
    val uploadingPageId: String? = null,
    /** SH-211: what the manager typed as each presentation page's link (page id → text). */
    val presentationLinks: Map<String, String> = emptyMap(),
    /** SH-211: why a typed presentation link is not accepted (page id → problem). */
    val linkProblems: Map<String, PresentationLinkProblem> = emptyMap(),
    /** Shown once the person tried to save or publish. */
    val problems: Set<CourseProblem> = emptySet(),
    /** Saved or deleted: the screen goes back. */
    val done: Boolean = false,
    val message: UiText? = null,
    val error: UiText? = null
)

/**
 * Writing a course: pages (text, picture, video link), the quiz with its right answers (stored
 * apart, for managers only), who takes it and, for OHS courses, the details the regulation asks
 * for. A draft can be saved half-done; publishing checks everything and raises the version.
 */
class CourseEditorViewModel(
    private val workplaceId: String,
    private val courseId: String?,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getCourse: GetCourseForEditingUseCase,
    private val saveCourse: SaveCourseUseCase,
    private val deleteCourse: DeleteCourseUseCase,
    private val uploadImage: UploadCourseImageUseCase,
    private val newCourseId: NewCourseIdUseCase,
    private val getWorkplace: GetWorkplaceUseCase,
    private val uploadPresentation: UploadCoursePresentationUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(CourseEditorUiState(isNew = courseId == null))
    val uiState: StateFlow<CourseEditorUiState> = _uiState.asStateFlow()
    private var editorId: String? = null

    init {
        viewModelScope.launch {
            val user = getCurrentUser().first()
            val planActive = observePlanActive.activeAt(workplaceId).first()
            editorId = user?.uid
            val canEdit = user?.capabilitiesAt(workplaceId, planActive).orEmpty().shows(Gate.MANAGE_COURSES)
            val roles = getWorkplace(workplaceId).getOrNull()?.settings?.availableRoles.orEmpty()
            if (!canEdit) {
                _uiState.update { it.copy(isLoading = false, canEdit = false, roles = roles) }
                return@launch
            }
            if (courseId == null) {
                val course = Course(id = newCourseId(workplaceId), workplaceId = workplaceId, title = "", pages = listOf(CoursePage(newItemId("p"))))
                _uiState.update { it.copy(isLoading = false, canEdit = true, course = course, roles = roles) }
            } else {
                getCourse(workplaceId, courseId)
                    .onSuccess { (course, key) ->
                        _uiState.update { it.copy(isLoading = false, canEdit = true, course = course, correct = key.correct, roles = roles, isNew = false) }
                    }
                    .onFailure { e -> _uiState.update { it.copy(isLoading = false, canEdit = true, error = e.toUiText(), roles = roles) } }
            }
        }
    }

    private fun edit(transform: (Course) -> Course) = _uiState.update { s -> s.course?.let { s.copy(course = transform(it)) } ?: s }

    fun setTitle(value: String) = edit { it.copy(title = value.take(CourseRules.MAX_TITLE)) }
    fun setDescription(value: String) = edit { it.copy(description = value.take(CourseRules.MAX_DESCRIPTION)) }

    fun setKind(kind: CourseKind) = edit { course ->
        course.copy(
            kind = kind,
            ohs = if (kind.isOhs) (course.ohs ?: OhsCourseInfo(lessonHours = CourseRules.minLessonHours(kind))) else null,
            countsForReadiness = course.countsForReadiness || kind.isOhs
        )
    }

    fun setLessonHours(value: String) = edit { c -> c.copy(ohs = (c.ohs ?: OhsCourseInfo()).copy(lessonHours = value.filter(Char::isDigit).take(3).toIntOrNull() ?: 0)) }
    fun setInstructor(value: String) = edit { c -> c.copy(ohs = (c.ohs ?: OhsCourseInfo()).copy(instructorName = value.take(CourseRules.MAX_OHS_TEXT))) }
    fun setConsultation(value: String) = edit { c -> c.copy(ohs = (c.ohs ?: OhsCourseInfo()).copy(consultation = value.take(CourseRules.MAX_OHS_TEXT))) }

    // Pages

    fun addPage(kind: CoursePageKind) = edit { c ->
        if (c.pages.size >= CourseRules.MAX_PAGES) c else c.copy(pages = c.pages + CoursePage(newItemId("p"), kind))
    }

    fun updatePage(pageId: String, transform: (CoursePage) -> CoursePage) = edit { c ->
        c.copy(pages = c.pages.map { if (it.id == pageId) transform(it) else it })
    }

    fun setPageTitle(pageId: String, value: String) = updatePage(pageId) { it.copy(title = value.take(CourseRules.MAX_PAGE_TITLE)) }
    fun setPageBody(pageId: String, value: String) = updatePage(pageId) { it.copy(body = value.take(CourseRules.MAX_BODY)) }

    /**
     * SH-211: the page's formatted lesson as the editor has it now. [CoursePage.body] follows as
     * plain text; saving sets the version older apps show (CourseRules.forStorage).
     */
    fun setPageRich(pageId: String, document: RichDocument) = updatePage(pageId) {
        it.copy(rich = document, body = document.text.take(CourseRules.MAX_BODY))
    }

    /**
     * SH-211: the link typed for a presentation page: an accepted one becomes the page's
     * presentation (with the provider's embed address), anything else keeps why it is not
     * accepted beside the field. An uploaded file stays until a link is accepted.
     */
    fun setPresentationLink(pageId: String, value: String) {
        val text = value.take(CourseRules.MAX_URL)
        val result = text.trim().takeIf { it.isNotEmpty() }?.let { PresentationLinks.read(it) }
        _uiState.update { s -> s.copy(presentationLinks = s.presentationLinks + (pageId to text)) }
        when (result) {
            null -> {
                _uiState.update { it.copy(linkProblems = it.linkProblems - pageId) }
                updatePage(pageId) { page -> if (page.presentation?.isUpload == true) page else page.copy(presentation = null) }
            }
            is PresentationLinkResult.Accepted -> {
                _uiState.update { it.copy(linkProblems = it.linkProblems - pageId) }
                updatePage(pageId) { it.copy(presentation = result.presentation) }
            }
            is PresentationLinkResult.Refused -> {
                _uiState.update { it.copy(linkProblems = it.linkProblems + (pageId to result.problem)) }
                updatePage(pageId) { page -> if (page.presentation?.isUpload == true) page else page.copy(presentation = null) }
            }
        }
    }

    /** The link shown in a presentation page's field: what was typed, else the stored one. */
    fun presentationLinkText(page: CoursePage): String =
        _uiState.value.presentationLinks[page.id] ?: page.presentation?.takeIf { !it.isUpload }?.link.orEmpty()

    /**
     * SH-211: a presentation file picked for [pageId] ([pdfOnly]: the PDF export next to an
     * uploaded .pptx / .odp): checked by its bytes, uploaded to the workplace's folder, then the
     * page shows it. Replacing a link or a file: the new file wins.
     */
    fun onPresentationPicked(pageId: String, file: PickedFile?, pdfOnly: Boolean = false) {
        val course = _uiState.value.course ?: return
        if (file == null) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.documents_file_unreadable)) }
            return
        }
        _uiState.update { it.copy(uploadingPageId = pageId) }
        viewModelScope.launch {
            uploadPresentation(workplaceId, course.id, file.name, file.bytes, pdfOnly)
                .onSuccess { uploaded ->
                    updatePage(pageId) { page ->
                        val current = page.presentation?.takeIf { it.isUpload }
                        val presentation = if (pdfOnly && current != null) {
                            current.copy(pdfPath = uploaded.path, pdfName = uploaded.fileName)
                        } else {
                            CoursePresentation(
                                source = PresentationSource.UPLOAD, filePath = uploaded.path,
                                fileName = uploaded.fileName, fileType = uploaded.contentType
                            )
                        }
                        page.copy(presentation = presentation)
                    }
                    if (!pdfOnly) _uiState.update { it.copy(presentationLinks = it.presentationLinks - pageId, linkProblems = it.linkProblems - pageId) }
                }
                .onFailure { e ->
                    val mb = PresentationFiles.MAX_BYTES / (1024 * 1024)
                    val text = if (e is com.evoapps.evoshift.domain.model.InvalidDocumentException) {
                        UiText.Resource(if (pdfOnly) Res.string.course_presentation_pdf_invalid else Res.string.course_presentation_invalid, mb)
                    } else {
                        e.toDocumentUiText()
                    }
                    _uiState.update { it.copy(error = text) }
                }
            _uiState.update { it.copy(uploadingPageId = null) }
        }
    }

    /** Takes the page's presentation away (the PDF export only, with [pdfOnly]). */
    fun removePresentation(pageId: String, pdfOnly: Boolean = false) {
        updatePage(pageId) { page ->
            if (pdfOnly) page.copy(presentation = page.presentation?.copy(pdfPath = null, pdfName = null))
            else page.copy(presentation = null)
        }
        if (!pdfOnly) _uiState.update { it.copy(presentationLinks = it.presentationLinks - pageId, linkProblems = it.linkProblems - pageId) }
    }
    fun setPageUrl(pageId: String, value: String) = updatePage(pageId) { it.copy(url = value.trim().take(CourseRules.MAX_URL)) }

    fun movePage(pageId: String, delta: Int) = edit { c ->
        val index = c.pages.indexOfFirst { it.id == pageId }
        val target = index + delta
        if (index < 0 || target !in c.pages.indices) c
        else c.copy(pages = c.pages.toMutableList().apply { add(target, removeAt(index)) })
    }

    fun removePage(pageId: String) = edit { c -> c.copy(pages = c.pages.filterNot { it.id == pageId }) }

    /** A picture picked for [pageId]: checked and uploaded, then the page points at it. */
    fun onImagePicked(pageId: String, file: PickedFile?) {
        val course = _uiState.value.course ?: return
        if (file == null) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.documents_file_unreadable)) }
            return
        }
        _uiState.update { it.copy(uploadingPageId = pageId) }
        viewModelScope.launch {
            uploadImage(workplaceId, course.id, file.bytes)
                .onSuccess { path -> updatePage(pageId) { it.copy(imagePath = path) } }
                .onFailure { e ->
                    val text = if (e is com.evoapps.evoshift.domain.model.InvalidDocumentException) {
                        UiText.Resource(Res.string.course_image_invalid, CourseFiles.MAX_IMAGE_BYTES / (1024 * 1024))
                    } else {
                        e.toDocumentUiText()
                    }
                    _uiState.update { it.copy(error = text) }
                }
            _uiState.update { it.copy(uploadingPageId = null) }
        }
    }

    // Questions

    fun addQuestion() = edit { c ->
        if (c.questions.size >= CourseRules.MAX_QUESTIONS) c
        else c.copy(questions = c.questions + QuizQuestion(newItemId("q"), "", listOf("", "")))
    }

    private fun updateQuestion(questionId: String, transform: (QuizQuestion) -> QuizQuestion) = edit { c ->
        c.copy(questions = c.questions.map { if (it.id == questionId) transform(it) else it })
    }

    fun setQuestionText(questionId: String, value: String) = updateQuestion(questionId) { it.copy(text = value.take(CourseRules.MAX_QUESTION)) }

    fun setMulti(questionId: String, multi: Boolean) {
        updateQuestion(questionId) { it.copy(multi = multi) }
        // One right answer when only one may be picked.
        if (!multi) _uiState.update { s -> s.copy(correct = s.correct + (questionId to s.correct[questionId].orEmpty().take(1))) }
    }

    fun addOption(questionId: String) = updateQuestion(questionId) { q ->
        if (q.options.size >= CourseRules.MAX_OPTIONS) q else q.copy(options = q.options + "")
    }

    fun setOption(questionId: String, index: Int, value: String) = updateQuestion(questionId) { q ->
        q.copy(options = q.options.mapIndexed { i, o -> if (i == index) value.take(CourseRules.MAX_OPTION) else o })
    }

    fun removeOption(questionId: String, index: Int) {
        val question = _uiState.value.course?.questions?.firstOrNull { it.id == questionId } ?: return
        if (question.options.size <= CourseRules.MIN_OPTIONS) return
        updateQuestion(questionId) { q -> q.copy(options = q.options.filterIndexed { i, _ -> i != index }) }
        _uiState.update { s ->
            val shifted = s.correct[questionId].orEmpty().filter { it != index }.map { if (it > index) it - 1 else it }
            s.copy(correct = s.correct + (questionId to shifted))
        }
    }

    /** Marks option [index] right or not (single-answer questions keep one). */
    fun toggleCorrect(questionId: String, index: Int) {
        val question = _uiState.value.course?.questions?.firstOrNull { it.id == questionId } ?: return
        _uiState.update { s ->
            val current = s.correct[questionId].orEmpty()
            val next = when {
                index in current -> current - index
                question.multi -> (current + index).sorted()
                else -> listOf(index)
            }
            s.copy(correct = s.correct + (questionId to next))
        }
    }

    fun removeQuestion(questionId: String) {
        edit { c -> c.copy(questions = c.questions.filterNot { it.id == questionId }) }
        _uiState.update { it.copy(correct = it.correct - questionId) }
    }

    fun setPassPercent(value: String) = edit { it.copy(passPercent = value.filter(Char::isDigit).take(3).toIntOrNull() ?: 0) }

    // Who takes it

    fun toggleRole(role: String) = edit { c -> c.copy(roles = if (role in c.roles) c.roles - role else c.roles + role) }
    fun setAllNewHires(value: Boolean) = edit { it.copy(allNewHires = value) }
    fun setDueDays(value: String) = edit { it.copy(dueDays = value.filter(Char::isDigit).take(3).toIntOrNull() ?: 0) }
    fun setCountsForReadiness(value: Boolean) = edit { it.copy(countsForReadiness = value || it.kind.isOhs) }

    fun saveDraft() = save(publish = false)
    fun publish() = save(publish = true)

    private fun save(publish: Boolean) {
        val state = _uiState.value
        val course = state.course ?: return
        val editor = editorId ?: return
        if (state.isSaving) return
        _uiState.update { it.copy(isSaving = true, problems = emptySet()) }
        viewModelScope.launch {
            saveCourse(course, state.correct, publish, editor)
                .onSuccess { stored ->
                    _uiState.update {
                        it.copy(
                            isSaving = false, course = stored, isNew = false, done = true,
                            message = UiText.Resource(if (stored.isPublished) Res.string.course_published else Res.string.course_saved)
                        )
                    }
                }
                .onFailure { e ->
                    // What keeps a course from being saved is listed above the buttons; a snackbar
                    // only for anything else.
                    val problems = (e as? InvalidCourseException)?.problems.orEmpty()
                    _uiState.update {
                        it.copy(isSaving = false, problems = problems, error = if (problems.isEmpty()) e.toUiText() else null)
                    }
                }
        }
    }

    fun delete() {
        val course = _uiState.value.course ?: return
        if (_uiState.value.isNew) {
            _uiState.update { it.copy(done = true) }
            return
        }
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            deleteCourse(workplaceId, course.id)
                .onSuccess { _uiState.update { it.copy(isSaving = false, done = true, message = UiText.Resource(Res.string.course_deleted)) } }
                .onFailure { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } }
        }
    }

    fun messageShown() = _uiState.update { it.copy(error = null, message = null) }
}

// --- Taking a course ---------------------------------------------------------------------------

data class TakeCourseUiState(
    val isLoading: Boolean = true,
    val course: Course? = null,
    /** 0 until pages.size - 1: a page; pages.size: the quiz; pages.size + 1: the result. */
    val step: Int = 0,
    val progress: CourseProgress? = null,
    /** The reader's result so far (null: no attempt yet). */
    val completion: CourseCompletion? = null,
    val answers: Map<String, List<Int>> = emptyMap(),
    /** The OHS general instruction's statement, ticked by the employee before sending. */
    val confirmed: Boolean = false,
    val isSubmitting: Boolean = false,
    val result: CourseResult? = null,
    /** Pictures loaded so far (Storage path → bytes). */
    val images: Map<String, ByteArray> = emptyMap(),
    /** SH-211: uploaded presentations' PDFs loaded so far for the inline view (Storage path → bytes). */
    val files: Map<String, ByteArray> = emptyMap(),
    /** SH-211: the presentation file being downloaded to open (its Storage path). */
    val openingFile: String? = null,
    val today: LocalDate = LocalDate(2026, 1, 1),
    val error: UiText? = null
) {
    val pageCount: Int get() = course?.pages?.size ?: 0
    val onQuiz: Boolean get() = course != null && step == pageCount
    val onResult: Boolean get() = course != null && step > pageCount
    val passed: Boolean get() = completion?.passed == true
    val attemptsLeft: Int get() = completion?.attemptsLeftOn(today) ?: CourseRules.MAX_ATTEMPTS_PER_DAY

    /** Whether the answers may be sent: everything answered, the OHS statement ticked. */
    val canSubmit: Boolean
        get() = course != null && !isSubmitting && attemptsLeft > 0 &&
            course.questions.all { !answers[it.id].isNullOrEmpty() } &&
            (course.kind != CourseKind.OHS_GENERAL || confirmed)
}

/** One course, page by page, then the quiz (graded on the server) and the result. */
class TakeCourseViewModel(
    private val workplaceId: String,
    private val courseId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getCourse: GetCourseToTakeUseCase,
    private val saveProgress: SaveCourseProgressUseCase,
    private val submitCourse: SubmitCourseUseCase,
    private val loadImage: LoadCourseImageUseCase,
    private val loadPresentation: LoadCoursePresentationUseCase,
    private val opener: DocumentOpener,
    private val today: () -> LocalDate = ::systemToday
) : ViewModel() {
    private val _uiState = MutableStateFlow(TakeCourseUiState(today = today()))
    val uiState: StateFlow<TakeCourseUiState> = _uiState.asStateFlow()
    private var userId: String? = null
    private var canRecord = false

    init {
        viewModelScope.launch {
            val user = getCurrentUser().first() ?: return@launch
            userId = user.uid
            canRecord = user.capabilitiesAt(workplaceId).shows(Gate.TAKE_COURSES)
            getCourse(workplaceId, courseId, user.uid)
                .onSuccess { (course, progress, completion) ->
                    _uiState.update { it.copy(isLoading = false, course = course, progress = progress, completion = completion) }
                    onStep()
                }
                .onFailure { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
        }
    }

    fun next() = goTo(_uiState.value.step + 1)
    fun back() = goTo(_uiState.value.step - 1)

    fun goTo(step: Int) {
        val state = _uiState.value
        val max = if (state.result != null) state.pageCount + 1 else state.pageCount
        _uiState.update { it.copy(step = step.coerceIn(0, max)) }
        onStep()
    }

    /**
     * Records the page seen (the person's own progress; a draft preview records nothing). SH-211:
     * a presentation page counts once the presentation was shown or opened ([presentationViewed]).
     */
    private fun onStep() {
        val state = _uiState.value
        val course = state.course ?: return
        val page = course.pages.getOrNull(state.step) ?: return
        page.imagePath?.let(::image)
        if (page.kind == CoursePageKind.PRESENTATION) return
        record(page.id)
    }

    /** SH-211: the presentation of the page on show was shown in the course or opened. */
    fun presentationViewed() {
        val state = _uiState.value
        val page = state.course?.pages?.getOrNull(state.step) ?: return
        if (page.kind == CoursePageKind.PRESENTATION && page.id !in state.progress?.pagesSeen.orEmpty()) record(page.id)
    }

    /** SH-211: loads an uploaded presentation's PDF for the inline view (the web). */
    fun loadFile(path: String) {
        if (path in _uiState.value.files) return
        viewModelScope.launch {
            loadPresentation(path).onSuccess { bytes -> _uiState.update { it.copy(files = it.files + (path to bytes)) } }
                .onFailure { e -> _uiState.update { it.copy(error = e.toDocumentUiText()) } }
        }
    }

    /**
     * SH-211: downloads the page's uploaded presentation ([pdf]: its PDF export) with the
     * person's sign-in and hands it to the device's viewer (DocumentOpener: the system's app,
     * Quick Look, a download on the web). The file never goes to an outside viewer.
     */
    fun openPresentationFile(pdf: Boolean = false) {
        val state = _uiState.value
        val presentation = state.course?.pages?.getOrNull(state.step)?.presentation?.takeIf { it.isUpload } ?: return
        val path = (if (pdf) presentation.pdfPath else presentation.filePath) ?: return
        val name = (if (pdf) presentation.pdfName else presentation.fileName) ?: "presentation"
        if (state.openingFile != null) return
        _uiState.update { it.copy(openingFile = path) }
        viewModelScope.launch {
            val bytes = state.files[path]?.let { Result.success(it) } ?: loadPresentation(path)
            val type = if (pdf) com.evoapps.evoshift.domain.model.DocumentFiles.PDF else presentation.fileType.orEmpty()
            bytes.mapCatching { opener.open(name, type, it) }
                .onSuccess { presentationViewed() }
                .onFailure { e -> _uiState.update { it.copy(error = e.toDocumentUiText()) } }
            _uiState.update { it.copy(openingFile = null) }
        }
    }

    private fun record(pageId: String) {
        val course = _uiState.value.course ?: return
        val uid = userId ?: return
        if (!canRecord || !course.isPublished) return
        val page = course.pages.firstOrNull { it.id == pageId } ?: return
        viewModelScope.launch {
            saveProgress(workplaceId, _uiState.value.progress, course.id, uid, page.id)
                .onSuccess { progress -> _uiState.update { it.copy(progress = progress) } }
        }
    }

    private fun image(path: String) {
        if (path in _uiState.value.images) return
        viewModelScope.launch {
            loadImage(path).onSuccess { bytes -> _uiState.update { it.copy(images = it.images + (path to bytes)) } }
        }
    }

    fun toggleAnswer(questionId: String, index: Int) {
        val question = _uiState.value.course?.questions?.firstOrNull { it.id == questionId } ?: return
        _uiState.update { s ->
            val current = s.answers[questionId].orEmpty()
            val next = when {
                !question.multi -> listOf(index)
                index in current -> current - index
                else -> (current + index).sorted()
            }
            s.copy(answers = s.answers + (questionId to next))
        }
    }

    fun setConfirmed(value: Boolean) = _uiState.update { it.copy(confirmed = value) }

    fun submit() {
        val state = _uiState.value
        val course = state.course ?: return
        if (!state.canSubmit) return
        _uiState.update { it.copy(isSubmitting = true) }
        viewModelScope.launch {
            submitCourse(course, state.answers, state.confirmed)
                .onSuccess { result ->
                    _uiState.update {
                        it.copy(isSubmitting = false, result = result, completion = result.completion, step = it.pageCount + 1)
                    }
                }
                .onFailure { e -> _uiState.update { it.copy(isSubmitting = false, error = e.toUiText()) } }
        }
    }

    /** After a failed attempt: the quiz again, answers cleared. */
    fun retry() = _uiState.update { it.copy(answers = emptyMap(), confirmed = false, result = null, step = it.pageCount) }

    fun messageShown() = _uiState.update { it.copy(error = null) }
}

// --- Progress (managers) ---------------------------------------------------------------------------

data class CourseProgressUiState(
    val isLoading: Boolean = true,
    val canManage: Boolean = false,
    val report: CourseProgressReport? = null,
    /** People who work here, for the assign dialog (uid → name). */
    val team: List<Pair<String, String>> = emptyList(),
    val assigning: Boolean = false,
    val selected: Set<String> = emptySet(),
    val dueOn: String = "",
    val today: LocalDate = LocalDate(2026, 1, 1),
    val message: UiText? = null,
    val error: UiText? = null
)

/** Who a course is for and how far each got; managers assign people personally. */
class CourseProgressViewModel(
    private val workplaceId: String,
    private val courseId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getCourse: GetCourseToTakeUseCase,
    private val getReport: GetCourseProgressReportUseCase,
    private val assignCourse: AssignCourseUseCase,
    private val users: UserRepository,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val today: () -> LocalDate = ::systemToday
) : ViewModel() {
    private val _uiState = MutableStateFlow(CourseProgressUiState(today = today()))
    val uiState: StateFlow<CourseProgressUiState> = _uiState.asStateFlow()
    private var managerId: String? = null

    init {
        viewModelScope.launch {
            val user = getCurrentUser().first()
            val planActive = observePlanActive.activeAt(workplaceId).first()
            managerId = user?.uid
            val capabilities = user?.capabilitiesAt(workplaceId, planActive).orEmpty()
            if (!capabilities.shows(Gate.COURSE_PROGRESS)) {
                _uiState.update { it.copy(isLoading = false) }
                return@launch
            }
            _uiState.update { it.copy(canManage = capabilities.shows(Gate.MANAGE_COURSES)) }
            refresh()
        }
    }

    private suspend fun refresh() {
        val course = getCourse(workplaceId, courseId, managerId.orEmpty()).getOrElse { e ->
            _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
            return
        }.first
        getReport(course)
            .onSuccess { report ->
                val team = users.getAssignableEmployees(workplaceId).getOrDefault(emptyList())
                    .sortedBy { it.displayName.lowercase() }.map { it.uid to it.displayName }
                _uiState.update { it.copy(isLoading = false, report = report, team = team) }
            }
            .onFailure { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
    }

    fun openAssign() = _uiState.update { it.copy(assigning = true, selected = emptySet(), dueOn = "") }
    fun closeAssign() = _uiState.update { it.copy(assigning = false) }
    fun toggleSelected(uid: String) = _uiState.update { s -> s.copy(selected = if (uid in s.selected) s.selected - uid else s.selected + uid) }
    fun setDueOn(value: String) = _uiState.update { it.copy(dueOn = value) }

    fun assign() {
        val state = _uiState.value
        val manager = managerId ?: return
        if (state.selected.isEmpty()) return
        val dueOn = state.dueOn.trim().takeIf { it.isNotEmpty() }?.let { runCatching { LocalDate.parse(it) }.getOrNull() ?: return }
        _uiState.update { it.copy(assigning = false) }
        viewModelScope.launch {
            assignCourse(workplaceId, courseId, state.selected, dueOn, manager)
                .onSuccess {
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.course_assigned_message, state.selected.size)) }
                    refresh()
                }
                .onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
        }
    }

    fun messageShown() = _uiState.update { it.copy(error = null, message = null) }
}

// --- The OHS training record ---------------------------------------------------------------------

/** What a manager is recording (the dialog's fields as typed). */
sealed interface OhsDialog {
    data class Settings(val group: OhsWorkerGroup?, val stationRequired: Boolean) : OhsDialog
    data class GeneralInPerson(val conductedBy: String = "", val completedOn: String = "") : OhsDialog
    data class Station(val position: String = "", val conductedBy: String = "", val conductedOn: String = "", val admittedOn: String = "") : OhsDialog
    data class PeriodicExam(val examOn: String = "", val commission: String = "", val certificateNumber: String = "") : OhsDialog
    data class PeriodicElsewhere(val examOn: String = "", val organizer: String = "", val certificateNumber: String = "") : OhsDialog
}

data class OhsRecordUiState(
    val isLoading: Boolean = true,
    val record: OhsRecord? = null,
    val personName: String = "",
    val isMine: Boolean = false,
    val canRecord: Boolean = false,
    val canSee: Boolean = false,
    /**
     * SH-244: the person is the owner (the employer): no initial training (a note instead), only
     * the periodic training, which employers take too.
     */
    val isOwner: Boolean = false,
    val dialog: OhsDialog? = null,
    val isSaving: Boolean = false,
    /** A document link to open (the screen hands it to the system). */
    val openUrl: String? = null,
    val opening: OhsDocumentKind? = null,
    val message: UiText? = null,
    val error: UiText? = null
)

/**
 * A person's OHS training (karta szkolenia wstępnego and periodic training): managers record the
 * in-person parts and the exam, the person confirms the instructions; both print the documents.
 */
class OhsRecordViewModel(
    private val workplaceId: String,
    private val userId: String?,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getOhsRecord: GetOhsRecordUseCase,
    private val recordOhs: RecordOhsTrainingUseCase,
    private val confirmOhs: ConfirmOhsTrainingUseCase,
    private val getDocument: GetOhsDocumentUseCase,
    private val users: UserRepository,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(OhsRecordUiState())
    val uiState: StateFlow<OhsRecordUiState> = _uiState.asStateFlow()
    private var personId: String? = null

    init {
        viewModelScope.launch {
            val user = getCurrentUser().first() ?: return@launch
            val planActive = observePlanActive.activeAt(workplaceId).first()
            val capabilities = user.capabilitiesAt(workplaceId, planActive)
            val target = userId ?: user.uid
            personId = target
            val mine = target == user.uid
            val canSee = if (mine) capabilities.shows(Gate.TAKE_COURSES) else capabilities.shows(Gate.COURSE_PROGRESS)
            val person = if (mine) user else users.getUserProfile(target).getOrNull()
            val isOwner = person != null && !OhsRules.appliesTo(person, workplaceId)
            _uiState.update {
                it.copy(
                    personName = person?.displayName.orEmpty(), isMine = mine, canSee = canSee, isOwner = isOwner,
                    canRecord = capabilities.shows(Gate.MANAGE_COURSES)
                )
            }
            if (!canSee) {
                _uiState.update { it.copy(isLoading = false) }
                return@launch
            }
            getOhsRecord(workplaceId, target)
                .onSuccess { record -> _uiState.update { it.copy(isLoading = false, record = record) } }
                .onFailure { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
        }
    }

    fun open(dialog: OhsDialog) = _uiState.update { it.copy(dialog = dialog) }
    fun edit(dialog: OhsDialog) = _uiState.update { it.copy(dialog = dialog) }
    fun closeDialog() = _uiState.update { it.copy(dialog = null) }

    /** The dialog as an entry, or null while something is missing or not a day. */
    fun entryOf(dialog: OhsDialog): OhsEntry? {
        fun day(value: String) = runCatching { LocalDate.parse(value.trim()) }.getOrNull()
        return when (dialog) {
            is OhsDialog.Settings -> dialog.group?.let { OhsEntry.Settings(it, dialog.stationRequired || it == OhsWorkerGroup.BLUE_COLLAR) }
            is OhsDialog.GeneralInPerson -> {
                val on = day(dialog.completedOn) ?: return null
                dialog.conductedBy.trim().takeIf { it.isNotEmpty() }?.let { OhsEntry.GeneralInPerson(it, on) }
            }
            is OhsDialog.Station -> {
                val conducted = day(dialog.conductedOn) ?: return null
                val admitted = day(dialog.admittedOn) ?: return null
                if (dialog.position.isBlank() || dialog.conductedBy.isBlank() || admitted < conducted) null
                else OhsEntry.Station(dialog.position.trim(), dialog.conductedBy.trim(), conducted, admitted)
            }
            is OhsDialog.PeriodicExam -> {
                val on = day(dialog.examOn) ?: return null
                dialog.commission.trim().takeIf { it.isNotEmpty() }?.let { OhsEntry.PeriodicExam(on, it, dialog.certificateNumber.trim()) }
            }
            is OhsDialog.PeriodicElsewhere -> {
                val on = day(dialog.examOn) ?: return null
                if (dialog.organizer.isBlank() || dialog.certificateNumber.isBlank()) null
                else OhsEntry.PeriodicElsewhere(on, dialog.organizer.trim(), dialog.certificateNumber.trim())
            }
        }
    }

    fun save() {
        val dialog = _uiState.value.dialog ?: return
        val entry = entryOf(dialog) ?: return
        val target = personId ?: return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            recordOhs(workplaceId, target, entry)
                .onSuccess { record -> _uiState.update { it.copy(isSaving = false, dialog = null, record = record, message = UiText.Resource(Res.string.ohs_recorded)) } }
                .onFailure { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } }
        }
    }

    /** The person confirms a recorded instruction (electronic confirmation, § 12 ust. 1). */
    fun confirm(part: OhsConfirmation) {
        if (_uiState.value.isSaving) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            confirmOhs(workplaceId, part)
                .onSuccess { record -> _uiState.update { it.copy(isSaving = false, record = record, message = UiText.Resource(Res.string.ohs_confirmed)) } }
                .onFailure { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } }
        }
    }

    fun document(kind: OhsDocumentKind) {
        val target = personId ?: return
        if (_uiState.value.opening != null) return
        _uiState.update { it.copy(opening = kind) }
        viewModelScope.launch {
            getDocument(workplaceId, target, kind)
                .onSuccess { link -> _uiState.update { it.copy(opening = null, openUrl = link.url) } }
                .onFailure { e -> _uiState.update { it.copy(opening = null, error = e.toUiText()) } }
        }
    }

    fun urlOpened() = _uiState.update { it.copy(openUrl = null) }

    fun messageShown() = _uiState.update { it.copy(error = null, message = null) }
}
